<template>
  <div class="block-teaser-blog">
    <FilterBlogs @set-blog-filter-relationals="setBlogFilterRelationals" />

    <div class="block-teaser-blog__pretitle">
      {{ data.pretitle }}
    </div>

    <div class="block-teaser-blog__title">
      {{ data.title }}
    </div>

    <div class="block-teaser-blog__teaser-wrapper">
      <div
        class="block-teaser-blog__big-teaser"
      >
        <BlockTeaserBlogCard
          v-for="item in firstArticle"
          :key="item.uuid"
          :data="item"
          :is-first-article="true"
        />
      </div>

      <ClientOnly>
        <masonry-wall
          class="block-teaser-blog__masonry"
          :items="blogEntries"
          :min-columns="isMobile ? 1 : 3"
          :column-width="300"
          :gap="isMobile ? 20 : 34"
        >
          <template #default="{ item, index }">
            <BlockTeaserBlogCard
              :key="item._uid"
              :data="item"
              :index="index"
            />
          </template>
        </masonry-wall>
      </ClientOnly>

      <div
        class="block-teaser__btn-load-more"
      >
        <AtomLoadMore
          :button-text="loadMoreText"
          :max-item-amount="response?.total"
          :current-item-amount="response?.articles?.length + 1"
          :is-loading="false"
          @load-more="loadMore()"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
const props = defineProps({
    data: {
        type: Object,
        default: () => {},
    },
});

const rawQuery = reactive({
    byUUID: null,
});

const clientDeviceStore = useClientDeviceStore();
const isMobile = computed(() => clientDeviceStore.isMobile);

const pageSize = computed(() => parseInt(props.data.show_items_per_page, 10));
const currentPage = ref(1);
const storyblokApi = useStoryblokApi();

const loadMoreText = computed(() => (useTranslation('general', 'load_more')));

/*
  Get the data from the API
  If there is a filter, we need to get the data from the filter
  If there is no filter, we need to get the data from the first 7 blogs
*/
const blogEntries = ref([]);
const firstArticle = ref([]);
const { data: response, refresh } = useLazyAsyncData(
    `blogs/${getUid()}`,
    async () => {
        const filters = rawQuery.byUUID ? {
            __or: [
                {
                    indications: {
                        in_array: rawQuery.byUUID.toString(),
                    },
                },
                {
                    focustopics: {
                        in_array: rawQuery.byUUID.toString(),
                    },
                },
                {
                    dossiers: {
                        in_array: rawQuery.byUUID.toString(),
                    },
                },
            ],
        } : null;

        const sbRes = await storyblokApi.get('cdn/stories/', {
            starts_with: 'blogs/',
            version: 'published',
            is_startpage: false,
            page: currentPage.value,
            per_page: pageSize.value,
            sort_by: 'first_published_at:desc',
            filter_query: rawQuery?.byUUID?.length > 0 ? filters : null,
        });

        blogEntries.value = currentPage.value === 1
            ? sbRes.data.stories
            : blogEntries.value.concat(sbRes.data.stories);

        if (currentPage.value === 1) {
            firstArticle.value = [blogEntries.value.shift()] || [];
        }

        return {
            firstArticle,
            articles: blogEntries.value,
            total: sbRes.total,
            perPage: sbRes.perPage,
        };
    },
);

/*
  When the filter is changed, we need to get the data again
*/
const setBlogFilterRelationals = (data) => {
    currentPage.value = 1;
    rawQuery.byUUID = data.values;
    refresh();
};

/*
  Load More
*/
const loadMore = () => {
    currentPage.value += 1;
    refresh();
};
</script>

<style lang="scss" scoped>
.block-teaser-blog {
    @include grid-columns();
    @include fluid('margin-bottom', 60px, 120px);

    display: flex;
    flex-direction: column;
    color: $C_GREEN_DARK;
}

.block-teaser-blog__pretitle {
    @include typo-font('light');
    @include typo-size('pretitleblog');
}

.block-teaser-blog__title {
    @include typo-font('regular');
    @include typo-size('lead');

    margin-bottom: 16px;

    @include mobile {
        margin-bottom: 20px;
    }
}

.block-teaser-blog__teaser-wrapper {
    @include grid-layout();

}

.block-teaser-blog__big-teaser {
    grid-column: span 6;
}

.block-teaser-blog__masonry {
    grid-column: span 12;

    @include mobile {
        grid-column: span 6;
    }
}

.block-teaser__btn-load-more {
    @include grid-columns(1, var(--grid-columns));

    display: flex;
    justify-content: center;
    margin-top: 10px;
}
</style>
